import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useReducer, useState } from 'react';
import { ChevronLeft } from '@mui/icons-material';
import { CircularProgress, Grid, IconButton } from '@mui/material';
import Dropzone, { FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';
import API from 'api/API';
import { SetDraftResponse } from 'api/Responses/SetResponses';
import AsyncImage from 'components/AsyncImage/AsyncImage';
import { RowAlignCenter } from 'components/Basic/Basic.styles';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { FileUploadContainer, FileUploadRoot } from 'components/FileUpload/FileUpload.styles';
import FormControlLabel from 'components/FormControl/FormControlLabel';
import InputField from 'components/InputField';
import Label from 'components/Label/Label';
import ReferenceImageGeneratorModal from 'components/ReferenceImageGenerator';
import { Tag } from 'components/ReferenceImageGenerator/molecules/ImageGeneratorStage';
import { TrayAttributesContainer, TrayDetailsContainer } from 'components/TrayDetails/TrayDetails.styles';
import { useLanguage } from 'context/AppContext';
import useUploadTrayReferenceImage from 'hooks/useUploadTrayReferenceImage';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { trayActions } from 'reducers/TrayReducer/trayActions';
import trayReducer, { initialTray } from 'reducers/TrayReducer/trayReducer';
import { ImageService } from 'services/FileService';
import { useUser } from 'store/user/useUser';
import { getGeneratedTempTrayRefImageFromFirebase, StorageType } from 'utils/helpers/image.helpers';
import { ModalContentType } from '../SetModal';
import { SetModalButtonContainer, SetModalHeader, SetModalWide } from '../SetModal.styles';

interface SelectTrayProps {
  set: SetDraftResponse;
  close: () => void;
  dispatchDraft: Dispatch<SetAction>;
  setModalContent: Dispatch<SetStateAction<ModalContentType>>;
}

// Workflow to add a new tray to a set
const SelectTray: FC<SelectTrayProps> = ({ set, close, dispatchDraft, setModalContent }) => {
  const [language] = useLanguage();
  const [newTray, dispatchNewTray] = useReducer(trayReducer, initialTray);
  const [openImageEditor, setOpenImageEditor] = useState(false);
  const [updateImage, setUpdateImage] = useState<number>(1);
  const [imageUrl, setImageUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const user = useUser();
  const { addNewTray, onSaveTaggedImageToFirebase } = useUploadTrayReferenceImage(set, newTray);
  const [isSavingAnnotation, setIsSavingAnnotation] = useState<boolean>(false);
  const isAddNewTraySubmitButtonDisabled =
    isLoading || isSavingAnnotation || !newTray.image_file || !newTray?.name?.trim() || !newTray?.reference?.trim();

  const onSaveTaggedImage = useCallback(
    async (tags: Tag[], taggedImage: string, originalImage: string) => {
      // blocks Add until the old temp image is replaced, so addNewTray never reads a deleted file
      setIsSavingAnnotation(true);
      try {
        const imagePath = await onSaveTaggedImageToFirebase(taggedImage);
        // best-effort cleanup of the temp image being replaced
        if (newTray.image_file) ImageService.deleteImage(newTray.image_file).catch(() => undefined);
        dispatchNewTray(trayActions.image(imagePath, originalImage, tags));
        setOpenImageEditor(false);
        setUpdateImage(updateImage + 1);
        toast.success(language.AddModal.Tray.annotateSuccess);
      } catch (error) {
        API.errorHandler(error);
      } finally {
        setIsSavingAnnotation(false);
      }
    },
    [language.AddModal.Tray.annotateSuccess, newTray.image_file, onSaveTaggedImageToFirebase, updateImage],
  );

  // Adds the defined tray to the set, closes the modal, and resets the modal state after animation
  const handleSubmit = useCallback(async (): Promise<void> => {
    try {
      // start button loading animation
      setIsLoading(true);

      const newTray = await addNewTray();
      dispatchDraft(draftActions.trayNew(newTray));
    } catch (error) {
      API.errorHandler(error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setModalContent('selectItem'), 500);
      close();
    }
  }, [addNewTray, close, dispatchDraft, setModalContent]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], error: FileRejection[]) => {
      try {
        // Throw an error if the dropper returns one
        if (error.length > 0 || !user.isAuthenticated) {
          throw new Error(error[0].errors[0].message);
        }
        // Generate image path
        const imagePath = getGeneratedTempTrayRefImageFromFirebase(acceptedFiles[0].name);

        // Upload image to firebase
        await ImageService.saveImage(imagePath, acceptedFiles[0]);

        // Store the new image path in the reducer
        dispatchNewTray(trayActions.image(imagePath, acceptedFiles[0].name, []));

        // Log successful upload
        toast.success(language.AddModal.Tray.uploadSuccess);
      } catch (error) {
        // Log any errors
        API.errorHandler(error, language.AddModal.Tray.uploadFailed);
      }
    },
    [user, language.AddModal.Tray.uploadSuccess, language.AddModal.Tray.uploadFailed],
  );

  // Only fetch the image for the editor while it is open
  useEffect(() => {
    const imagePath = newTray.image_file;
    if (!openImageEditor || !imagePath) return undefined;
    let objectUrl: string | undefined;
    let cancelled = false;
    const fetchImage = async () => {
      try {
        // same-origin blob URL so the Konva canvas is not tainted and toDataURL works
        const blob = await ImageService.getImageBlob(imagePath);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch (error) {
        if (cancelled) return;
        // a cross-origin fallback would taint the canvas and fail on save, so close the editor instead
        API.errorHandler(error, language.ReferenceImageModal.imageLoadError);
        setOpenImageEditor(false);
      }
    };
    void fetchImage();
    return () => {
      cancelled = true;
      setImageUrl(undefined);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [openImageEditor, newTray.image_file, language.ReferenceImageModal.imageLoadError]);

  return (
    <>
      <SetModalWide>
        <RowAlignCenter>
          <IconButton
            sx={{ position: 'absolute !important', top: '1rem', left: '1rem' }}
            onClick={() => setModalContent('selectItem')}
          >
            <ChevronLeft />
          </IconButton>
          <SetModalHeader>{language.AddModal.Tray.add}</SetModalHeader>
        </RowAlignCenter>
        <TrayDetailsContainer>
          {newTray.image_file ? (
            <AsyncImage
              src={newTray.image_file}
              alt={newTray.reference}
              updateTrigger={updateImage}
              openAnnotateImage={() => setOpenImageEditor(true)}
              imgWidth={19}
              imgHeight={14}
              storageType={StorageType.FIREBASE}
            />
          ) : (
            <Dropzone onDrop={onDrop} accept="image/*" maxFiles={1}>
              {({ getRootProps, getInputProps }) => (
                <FileUploadContainer>
                  <FileUploadRoot {...getRootProps()}>
                    <input {...getInputProps()} />
                    <p>{language.AddModal.Tray.upload}</p>
                  </FileUploadRoot>
                </FileUploadContainer>
              )}
            </Dropzone>
          )}
          <TrayAttributesContainer>
            <Label text={language.TrayTable.reference}>
              <InputField
                variant="outlined"
                fullWidth
                placeholder={language.AddModal.Tray.referencePlaceholder}
                value={newTray.reference}
                onChange={e => dispatchNewTray(trayActions.ref(e.target.value))}
              />
            </Label>
            <Label text={language.TrayTable.name}>
              <InputField
                variant="outlined"
                fullWidth
                placeholder={language.AddModal.Tray.namePlaceholder}
                value={newTray.name}
                onChange={e => dispatchNewTray(trayActions.name(e.target.value))}
              />
            </Label>
            <Grid container spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newTray.non_standard}
                    onChange={() => dispatchNewTray(trayActions.nonStandard(!newTray.non_standard))}
                  />
                }
                label={language.TrayTable.nonStandard}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newTray.general_test}
                    onChange={() => dispatchNewTray(trayActions.generalTest(!newTray.general_test))}
                  />
                }
                label={language.TrayTable.generalInspection}
              />
            </Grid>
          </TrayAttributesContainer>
        </TrayDetailsContainer>
        <SetModalButtonContainer>
          <Button fullWidth onClick={handleSubmit} disabled={isAddNewTraySubmitButtonDisabled}>
            {isLoading ? <CircularProgress /> : <>{language.CreateModal.add}</>}
          </Button>
        </SetModalButtonContainer>
      </SetModalWide>
      <ReferenceImageGeneratorModal
        imageUrl={imageUrl}
        modalOpen={openImageEditor}
        setClosed={() => setOpenImageEditor(false)}
        originalImage={newTray.original_image_file ?? newTray.image_file ?? ''}
        existingTags={newTray.reference_tags_metadata}
        onSaveTaggedImage={onSaveTaggedImage}
      />
    </>
  );
};

export default SelectTray;
